/**
 * What the recorded voice says.
 *
 * Every clip in the voice pack has a key: `p:<phoneme>` for a speech sound,
 * `w:<word>` for a word, `x:<line>` for one of the lines below. The engines
 * speak through these keys, so the sound of "sh" and the praise after it come
 * from the same voice. Anything without a clip is spoken by a voice line
 * made for the material in that same voice, or not at all.
 */

import { asWritten, PHONEMES, SPOKEN_WORDS, WORDS } from './phonics'
import { NUMBER_WORDS, STARTER_DECKS } from './starterDecks'

/** Lines the reading engines speak. Short, warm, and never a verdict. */
export const VOICE_LINES: Record<string, string> = {
  slide: 'Slide your finger under the letters.',
  what: "What's the word?",
  say: 'Say it out loud!',
  which: 'Which picture is it?',
  listen: 'Listen.',
  again: "Let's listen again.",
  tap: 'Tap a letter to hear its sound.',
  yes1: 'Yes!',
  yes2: 'You read it!',
  yes3: 'Wonderful reading!',
  yes4: "That's it!",
  yes5: 'You did it!',
  close: 'So close. Listen again.',
  done: 'You read all of them!',
  shelf: 'Look at all the words you read.',
  thisis: 'This word is',
  find: 'Find the word',
  tapit: 'Tap it!',
  caught: 'Look at all the words you found.',
  // Letters in the sight engine. Each leads into a sound said after it
  // ("Which letter says" mmm), so none closes with punctuation.
  lettersays: 'This letter says',
  whichletter: 'Which letter says',
  startswith: 'Which one starts with',
  caughtsounds: 'Look at all the sounds you found.',
  readpage: 'Read this page out loud.',
  theend: 'The end!',
  reader: 'What a reader!',
  build: 'Pick a letter to make a new word.',
  rhyme: 'Listen! They all rhyme.',
  levelup: 'Bigger words now. You are ready!',
  // The cards engine. A line with no closing punctuation leads into a word
  // said after it ("What comes after" five), so a question is built from
  // recorded parts, since there is no voice for a whole unrecorded question.
  next: 'What comes next?',
  howmany: 'How many?',
  rhymeswith: 'What rhymes with',
  onemore: 'One more look!',
  alldone: 'All done!',
  whatafter: 'What comes after',
  whatbefore: 'What comes before',
  comesafter: 'comes after',
  comesbefore: 'comes before',
  plus: 'plus',
  takeaway: 'take away',
  is: 'is',
}

/** Every clip the pack should hold, with the text and how it should be said. */
export interface VoiceClip {
  key: string
  text: string
  kind: 'phoneme' | 'word' | 'line'
  /** For a phoneme: a word it is heard in, and whether it can be held. */
  as?: string
  held?: boolean
}

export function voiceClips(): VoiceClip[] {
  const words = new Set<string>()
  const clips: VoiceClip[] = PHONEMES.map((p) => ({ key: `p:${p.id}`, text: p.say, kind: 'phoneme' as const, as: p.as, held: p.held }))
  for (const w of [...WORDS.map((w) => w.word), ...SPOKEN_WORDS.map((w) => w.toLowerCase()), ...NUMBER_WORDS]) {
    if (words.has(w)) continue
    words.add(w)
    clips.push({ key: `w:${w}`, text: asWritten(w), kind: 'word' })
  }
  for (const [id, text] of Object.entries(VOICE_LINES)) clips.push({ key: `x:${id}`, text, kind: 'line' })
  // A starter deck's name, announced at its turn.
  for (const d of STARTER_DECKS) clips.push({ key: deckTitleKey(d.title), text: `${d.title}!`, kind: 'line' })
  return clips
}

/**
 * The line a deck's title is recorded under. Keyed by the title itself, so
 * any deck with that name (a starter, or a family's own) is announced in the
 * voice, and a deck with another name never plays the wrong one. The cards
 * engine makes the same key (cards.js titleKey).
 */
export function deckTitleKey(title: string): string {
  return 'x:deck-' + title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}
